import 'react-quill-new/dist/quill.snow.css';
import './RichTextEditor.css';

import type { CSSProperties } from 'react';
import { forwardRef, useImperativeHandle, useRef } from 'react';
import ReactQuill from 'react-quill-new';

export interface RichTextEditorHandle {
  insertText: (text: string) => void;
}

interface RichTextEditorProps {
  disabled?: boolean;
  onChange?: (html: string) => void;
  placeholder?: string;
  style?: CSSProperties;
  value?: string;
}

const DEFAULT_MODULES = {
  toolbar: [
    ['bold', 'italic', 'underline'],
    [{ color: [] }, { background: [] }],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link'],
  ],
};

const RichTextEditor = forwardRef<RichTextEditorHandle, RichTextEditorProps>(
  ({ disabled, onChange, placeholder, style, value }, ref) => {
    const quillRef = useRef<ReactQuill>(null);

    useImperativeHandle(ref, () => ({
      insertText: (text: string) => {
        const quill = quillRef.current?.getEditor();

        if (quill) {
          const range = quill.getSelection(true);
          quill.insertText(range.index, text);
          quill.setSelection(range.index + text.length);
        }
      },
    }));

    const handleChange: ReactQuill['props']['onChange'] = (html, _delta, _source, editor) => {
      const isEmpty = !editor.getText().trim();
      onChange?.(isEmpty ? '' : html);
    };

    return (
      <ReactQuill
        className="RichTextEditor notranslate"
        modules={DEFAULT_MODULES}
        onChange={handleChange}
        placeholder={placeholder}
        readOnly={disabled}
        ref={quillRef}
        style={style}
        theme="snow"
        value={value || ''}
      />
    );
  },
);

RichTextEditor.displayName = 'RichTextEditor';

export default RichTextEditor;
